import XLSX from 'xlsx';
import { filter, findIndex, includes, isEmpty, isNil, map } from 'lodash';

import {
  getURL,
  getExcelSheet,
  getFirstRow,
  getLastRow,
  isExcel,
  isGoogleSheets,
  getExcelColumns,
  getFirstColumn,
  getLastColumn
} from '../api/Layers/layer-operations';

import adaptURL from './adapt-url';
import methodPromise from './method-promise-wrapper';
import { urlRegex } from './regex-utils';

/**
 * Fetch the Excel or Google Sheet stored in the URL.
 *
 * @async
 * @param {string} layer - The Excel or Google Sheet layer.
 * @returns {object} An object containing the two arrays needed for the table.
 * @throws {Error} If something goes wrong.
 */

const loadExcelFromURL = async layer => {
  const url = adaptURL(getURL(layer));
  const excelSheet = getExcelSheet(layer);

  try {
    if (isEmpty(url)) throw new Error('The url is empty');
    if (!url.match(urlRegex)) throw new Error('No identifiable url');
    let tableTitle = [];
    let tableBody = [];

    if (isExcel(layer)) {
      const response = await fetch(url);
      if (response.ok) {
        const buffer = await response.arrayBuffer();
        const data = new Uint8Array(buffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const worksheet = workbook.Sheets[excelSheet];
        const excelArray = filter(
          map(XLSX.utils.sheet_to_json(worksheet, { header: 1 }), row => {
            if (getFirstColumn(layer) === 1 && getLastColumn(layer) === 1) return row;
            return row.slice(getFirstColumn(layer) - 1, getLastColumn(layer));
          }),
          (row, index) =>
            !isEmpty(row) &&
            ((getFirstRow(layer) === 1 && getLastRow(layer) === 1) ||
              (index >= getFirstRow(layer) - 1 && index < getLastRow(layer)))
        );
        [tableTitle, ...tableBody] = excelArray;

        tableTitle = map(
          filter(tableTitle, title => !isNil(title)),
          title => `${title.trim()}`
        );
      }
    }

    if (isGoogleSheets(layer)) {
      const urlArray = url.split('/');
      const idIndex = findIndex(urlArray, path => path === 'd') + 1;

      const rows = await methodPromise('layers.loadGoogleSheets', {
        excelSheet,
        spreadsheetId: urlArray[idIndex]
      });
      const columns = isEmpty(getExcelColumns(layer)) ? rows[0] : getExcelColumns(layer);

      tableTitle = filter(rows[0], column => includes(columns, column));
      if (getFirstRow(layer) !== 1 || getLastRow(layer) !== 1) {
        tableTitle = filter(rows[getFirstRow(layer) - 1], (value, index) =>
          includes(columns, rows[0][index])
        );
        if (getLastRow(layer) < rows.length) rows.splice(getLastRow(layer));
      }
      tableTitle = map(
        filter(tableTitle, title => !isNil(title)),
        title => `${title.trim()}`
      );

      tableBody = filter(
        map(rows, row => filter(row, (value, index) => includes(columns, rows[0][index]))),
        row => !isEmpty(row)
      );
      tableBody.splice(0, getFirstRow(layer));
    }
    return { tableBody, tableTitle };
  } catch (error) {
    throw new Error(error.message);
  }
};

export default loadExcelFromURL;
